import {
    ArrowBack,
    CloudDone,
    CloudOff,
    Edit,
    Publish,
    Redo,
    Restore,
    Refresh,
    Save,
    Undo,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "app/providers/AuthProvider";
import UserSelect from "modules/fluss/components/UserSelect";
import FlussAutomationPanel from "modules/fluss/components/FlussAutomationPanel";
import FlussCanvas, { FlussCanvasHandle } from "modules/fluss/components/FlussCanvas";
import PendingIssuesPanel, { hasBlockingIssues } from "modules/fluss/components/PendingIssuesPanel";
import FlussNodePropertiesPanel, {
    useFlussNodePanelFeatureEnabled,
} from "modules/fluss/components/FlussNodePropertiesPanel";
import { FlussInlineIssuesProvider } from "modules/fluss/components/node-properties/FlussNodePanelField";
import {
    ConfirmActionDialog,
    FlussPageHeader,
    FlussStatusChip,
    LifecycleStepper,
    NodeValidationBadge,
    SidePanel,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { flussApi, flussApiError } from "modules/fluss/services/fluss-api";
import { FLUSS_NODE_TYPES, studioDraftXml } from "modules/fluss/utils/bpmn";
import { resolveCallActivityDefinitionId, CallActivityTargetReason } from "modules/fluss/utils/callActivityTarget";
import { openInternalTargetInNewTab } from "shared/services/navigationClick";
import {
    canAutomateFluss,
    canAuthorFlussScript,
    canDesignFluss,
    canDiscardFlussDraft,
    canViewFlussAdvanced,
} from "modules/fluss/utils/permissions";
import {
    FlussDefinition,
    FlussElementSelection,
    FlussStudioMode,
    FlussValidationError,
} from "modules/fluss/types/fluss";
import { AttachmentPanel } from "modules/record/shared/attachments/AttachmentPanel";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import TranslatableInput from "component/global/translation/TranslatableInput";
import {
    MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    clampPropertiesWidth,
    FLUSS_PROPERTIES_WIDTH_STORAGE_KEY,
    loadStoredPropertiesWidth,
} from "./fluss-panel-width";

const getProcessConfig = (modeler: any) => {
    const root = modeler?.get("canvas")?.getRootElement();
    const process = root?.businessObject?.bpmnElement;
    return process?.extensionElements?.values?.find((value: any) => value.$type === "fluss:Config");
};

const updateProcessConfig = (modeler: any, updates: Record<string, string>) => {
    if (!modeler) return;
    const root = modeler.get("canvas").getRootElement();
    const process = root?.businessObject?.bpmnElement;
    if (!process) return;
    const moddle = modeler.get("moddle");
    const modeling = modeler.get("modeling");
    let extensionElements = process.extensionElements;
    if (!extensionElements) {
        extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        modeling.updateModdleProperties(root, process, { extensionElements });
    }
    let config = getProcessConfig(modeler);
    if (!config) {
        config = moddle.create("fluss:Config", {});
        config.$parent = extensionElements;
        extensionElements.values = [...(extensionElements.values || []), config];
        modeling.updateModdleProperties(root, extensionElements, { values: extensionElements.values });
    }
    modeling.updateModdleProperties(root, config, updates);
};

// PRIM-009: the properties panel used to have a hard-coded 320px width (fluss-studio.scss);
// this makes it drag-resizable like the equivalent panel in Platform Studio / Service Portal
// Form Builder, and remembers the chosen width across sessions.

const lifecycleSteps = [
    { key: "fluss.lifecycle.draft", fallback: "Draft" },
    { key: "fluss.lifecycle.validate", fallback: "Validate" },
    { key: "fluss.lifecycle.publish", fallback: "Publish" },
];

const FlussStudioPage = () => {
    const { t } = useTranslation();
    const { definitionId = "" } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkedNodeIdRef = useRef(searchParams.get("node"));
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const canvasRef = useRef<FlussCanvasHandle | null>(null);
    const workspaceRef = useRef<HTMLDivElement | null>(null);
    const [propertiesWidth, setPropertiesWidth] = useState(() =>
        loadStoredPropertiesWidth(typeof window === "undefined" ? undefined : window.localStorage)
    );
    const [modeler, setModeler] = useState<any>(null);
    const [definition, setDefinition] = useState<FlussDefinition | null>(null);
    const [mode, setMode] = useState<FlussStudioMode>(
        searchParams.get("mode") === "automation" || searchParams.has("node") ? "automation" : "design"
    );
    const [selection, setSelection] = useState<FlussElementSelection | null>(null);
    const [dirty, setDirty] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [ownerUserId, setOwnerUserId] = useState("");
    const [raci, setRaci] = useState("");
    const [validationErrors, setValidationErrors] = useState<FlussValidationError[]>([]);
    const [publishPanelOpen, setPublishPanelOpen] = useState(false);
    const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    const [discarding, setDiscarding] = useState(false);
    const [discardError, setDiscardError] = useState<string | null>(null);
    const [conflict, setConflict] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const [canvasEpoch, setCanvasEpoch] = useState(0);
    const [callActivityOpenError, setCallActivityOpenError] = useState<string | null>(null);

    const canDesign = canDesignFluss(user);
    const canAutomate = canAutomateFluss(user);
    const canViewAdvanced = canViewFlussAdvanced(user);
    const canDiscardDraftPermission = canDiscardFlussDraft(user);
    const newNodePanelsEnabled = useFlussNodePanelFeatureEnabled(canDesign);

    const definitionQuery = useQuery({
        queryKey: ["fluss", "definitions", definitionId],
        queryFn: () => flussApi.getDefinition(definitionId),
        enabled: Boolean(definitionId) && canDesign,
    });

    const definitionsQuery = useQuery({
        queryKey: ["fluss", "node-panel", "definitions"],
        queryFn: flussApi.listDefinitions,
        enabled: canDesign,
    });

    const applyDefinitionData = useCallback((next: FlussDefinition) => {
        setDefinition(next);
        setName(next.name || "");
        setDescription(next.description || "");
        setOwnerUserId(next.owner_user_id || "");
        setConflict(false);
    }, []);

    useEffect(() => {
        if (!definitionQuery.data) return;
        applyDefinitionData(definitionQuery.data);
    }, [definitionQuery.data, applyDefinitionData]);

    // Discards local canvas state and re-imports the server's XML. Unlike the header's plain
    // "Refresh" (which only re-syncs side-panel fields so it never clobbers unsaved edits), this
    // is only reachable from the conflict banner, where the user has explicitly chosen to abandon
    // their possibly-conflicting local draft. Bumping canvasEpoch changes FlussCanvas's `key`,
    // forcing a full remount so bpmn-js re-imports the fresh xml_draft (it otherwise only imports
    // once on mount and ignores subsequent xml prop changes).
    const reloadFromServer = useCallback(async () => {
        const result = await definitionQuery.refetch();
        if (result.data) {
            applyDefinitionData(result.data);
            setCanvasEpoch((epoch) => epoch + 1);
            setDirty(false);
        }
    }, [definitionQuery, applyDefinitionData]);

    // Bumped on every real content edit (canvas or documentation fields). saveNow() snapshots this
    // at the moment it reads the XML/attributes to save; onSuccess only clears `dirty` if nothing
    // edited the model again while that save was in flight — otherwise it would silently drop the
    // newer edits from the "unsaved changes" tracking (fix for a reported autosave data-loss risk).
    const editVersionRef = useRef(0);
    const bumpEditVersion = useCallback(() => {
        editVersionRef.current += 1;
        setDirty(true);
    }, []);

    const [saveError, setSaveError] = useState<string | null>(null);

    const saveMutation = useMutation({
        mutationFn: async (payload: { xml: string; current: FlussDefinition; editVersion: number }) =>
            flussApi.saveDefinition(payload.current.id, {
                xml_draft: payload.xml,
                lock_version: payload.current.lock_version,
                name,
                description,
                owner_user_id: ownerUserId || null,
            }),
        onSuccess: (saved, variables) => {
            setDefinition(saved);
            if (variables.editVersion === editVersionRef.current) setDirty(false);
            setConflict(false);
            setSaveError(null);
            setLastSavedAt(new Date());
            queryClient.setQueryData(["fluss", "definitions", definitionId], saved);
            queryClient.invalidateQueries({ queryKey: ["fluss", "definitions"] });
        },
        onError: (error: any, variables) => {
            const { code, message } = flussApiError(error);
            if (code === "STALE_DRAFT") {
                setConflict(true);
                setSaveError(null);
            } else {
                const scriptForbidden = error?.response?.status === 403 && /<(?:\w+:)?scriptTask\b/.test(variables.xml);
                setSaveError(scriptForbidden
                    ? t("fluss.autosave.script_author_required", { defaultValue: "This draft contains a Script task; only a script author can save it. Remove the node or ask an author." })
                    : message || t("fluss.autosave.error", { defaultValue: "Could not save the draft." }));
            }
        },
    });

    // Multiple callers (autosave, manual Save, validate/publish) can ask to save at once. Rather
    // than firing overlapping requests (which would race on lock_version and needlessly bounce off
    // STALE_DRAFT), a save already in flight is awaited and its result reused instead of returning
    // a stale `definition` snapshot immediately (fix for a reported publish/validate race).
    const inFlightSaveRef = useRef<Promise<FlussDefinition | null> | null>(null);

    const saveNow = useCallback(async (): Promise<FlussDefinition | null> => {
        if (!definition || definition.status !== "draft") return definition;
        if (inFlightSaveRef.current) return inFlightSaveRef.current;

        const editVersionAtSave = editVersionRef.current;
        updateProcessConfig(modeler, {
            description,
            owner: ownerUserId,
            raci,
        });
        const xml = await canvasRef.current?.saveXml();
        if (!xml) return definition;

        const promise = saveMutation
            .mutateAsync({ xml, current: definition, editVersion: editVersionAtSave })
            .finally(() => {
                inFlightSaveRef.current = null;
            });
        inFlightSaveRef.current = promise;
        return promise;
    }, [definition, description, modeler, ownerUserId, raci, saveMutation]);

    useEffect(() => {
        if (!dirty || !definition || definition.status !== "draft" || conflict || saveError || discarding) return;
        const timer = window.setTimeout(() => {
            saveNow().catch(() => undefined);
        }, 1400);
        return () => window.clearTimeout(timer);
    }, [dirty, definition, conflict, saveError, discarding, name, description, ownerUserId, raci, saveNow]);

    const validateMutation = useMutation({
        onMutate: () => {
            setValidationErrors([]);
            canvasRef.current?.clearMarkers();
        },
        mutationFn: async () => {
            const current = dirty ? await saveNow() : definition;
            if (!current) throw new Error("definition unavailable");
            const xml = await canvasRef.current?.saveXml();
            return flussApi.validateDefinition(current.id, xml || current.xml_draft || "");
        },
        onSuccess: (result) => {
            setValidationErrors(result.errors);
            setPublishPanelOpen(true);
        },
    });

    const publishMutation = useMutation({
        mutationFn: async () => {
            const current = dirty ? await saveNow() : definition;
            if (!current) throw new Error("definition unavailable");
            return flussApi.publishDefinition(current.id);
        },
        onSuccess: ({ definition: published, errors }) => {
            setValidationErrors(errors);
            setDefinition(published);
            setConfirmPublishOpen(false);
            setPublishPanelOpen(hasBlockingIssues(errors));
            queryClient.setQueryData(["fluss", "definitions", definitionId], published);
            queryClient.invalidateQueries({ queryKey: ["fluss", "definitions"] });
        },
    });

    // AJUSTE 006: "Edit / New version" on a published definition clones it into a fresh
    // editable draft (v+1, append-only — Fluss::DraftService.new_version_from_active). The
    // definition being viewed here never changes; the studio navigates to the new draft.
    const [confirmNewVersionOpen, setConfirmNewVersionOpen] = useState(false);
    const newVersionMutation = useMutation({
        mutationFn: async () => flussApi.createNewVersion(definition!.definition_key),
        onSuccess: async (draft) => {
            setConfirmNewVersionOpen(false);
            // The router keeps this page mounted when only :definitionId changes. Drop
            // canvas/selection state from the published version before opening the draft.
            setSelection(null);
            setModeler(null);
            setValidationErrors([]);
            setDirty(false);
            deepLinkedNodeIdRef.current = null;
            await queryClient.invalidateQueries({ queryKey: ["fluss", "definitions"] });
            navigate(`/home/fluss/definitions/${draft.id}`);
        },
    });

    const discardDraft = useCallback(async () => {
        if (!definition) return;
        setDiscarding(true);
        setDiscardError(null);
        try {
            const current = inFlightSaveRef.current ? await inFlightSaveRef.current : definition;
            if (!current) return;
            const restored = await flussApi.discardDraft(current.id, { lock_version: current.lock_version });
            applyDefinitionData(restored);
            editVersionRef.current += 1;
            setDirty(false);
            setCanvasEpoch((epoch) => epoch + 1);
            queryClient.setQueryData(["fluss", "definitions", restored.id], restored);
            queryClient.removeQueries({ queryKey: ["fluss", "definitions", definitionId], exact: true });
            await queryClient.invalidateQueries({ queryKey: ["fluss", "definitions"] });
            setConfirmDiscardOpen(false);
            navigate(`/home/fluss/definitions/${restored.id}`);
            queryClient.removeQueries({ queryKey: ["fluss", "definitions", definitionId], exact: true });
        } catch (error) {
            const { code, message } = flussApiError(error);
            if (code === "STALE_DRAFT") {
                setConflict(true);
                setConfirmDiscardOpen(false);
            } else if (code === "FORBIDDEN") {
                setDiscardError(t("fluss.discard.forbidden", { defaultValue: "You are not allowed to discard this draft." }));
            } else if (code === "NO_ACTIVE_VERSION") {
                setDiscardError(t("fluss.discard.no_active_version", { defaultValue: "There is no published version to restore from." }));
            } else {
                setDiscardError(message || t("fluss.discard.error", { defaultValue: "Could not discard the draft." }));
            }
        } finally {
            setDiscarding(false);
        }
    }, [definition, applyDefinitionData, definitionId, navigate, queryClient, t]);

    const markDocumentChanged = (update?: () => void) => {
        update?.();
        bumpEditVersion();
    };

    const onModelerReady = (readyModeler: any) => {
        setModeler(readyModeler);
        const config = getProcessConfig(readyModeler);
        setRaci(config?.raci || "");
        const nodeId = deepLinkedNodeIdRef.current;
        const node = nodeId ? readyModeler.get("elementRegistry")?.get(nodeId) : null;
        if (node) {
            // Keep deep links deterministic even when bpmn-js suppresses a selection event
            // during the initial XML import.
            setSelection({
                id: node.id,
                type: node.type,
                businessObject: node.businessObject,
                element: node,
            });
        }
    };

    const changeMode = (next: FlussStudioMode) => {
        setMode(next);
        const params = new URLSearchParams(searchParams);
        params.set("mode", next);
        setSearchParams(params, { replace: true });
    };

    const changeSelection = (next: FlussElementSelection | null) => {
        if (!next && deepLinkedNodeIdRef.current && modeler?.get("elementRegistry")?.get(deepLinkedNodeIdRef.current)) return;
        deepLinkedNodeIdRef.current = next?.id || null;
        setSelection(next);
        const params = new URLSearchParams(searchParams);
        if (next) params.set("node", next.id);
        else params.delete("node");
        setSearchParams(params, { replace: true });
    };

    const syncSelectionWithModel = (elements: any[]) => {
        if (selection && modeler?.get("elementRegistry") && !modeler.get("elementRegistry").get(selection.id)) {
            changeSelection(null);
            return;
        }
        setSelection((prev) => {
            if (!prev) return prev;
            const element = elements.find((item) => item.id === prev.id);
            if (!element) return prev;
            return {
                id: element.id,
                type: element.type,
                businessObject: element.businessObject,
                element,
            };
        });
    };


    const goToValidationNode = (elementId: string) => {
        changeMode("automation");
        canvasRef.current?.focusElement(elementId);
        setPublishPanelOpen(false);
    };

    const callActivityErrorMessage = useCallback((reason: CallActivityTargetReason) => {
        if (reason === "missing_key") {
            return t("call_activity.open.missing_key", { defaultValue: "No referenced process is selected for this Call Activity." });
        }
        if (reason === "pinned_unavailable" || reason === "missing_pinned_version") {
            return t("call_activity.open.pinned_unavailable", { defaultValue: "The pinned process version is not published or is deactivated." });
        }
        return t("call_activity.open.active_unavailable", { defaultValue: "The referenced process has no active published version." });
    }, [t]);

    const openCallActivityTarget = useCallback((binding: any) => {
        const result = resolveCallActivityDefinitionId(definitionsQuery.data || [], binding);
        if (result.status === "resolved") {
            setCallActivityOpenError(null);
            openInternalTargetInNewTab(`/home/fluss/definitions/${result.id}`);
        } else {
            setCallActivityOpenError(callActivityErrorMessage(result.reason));
        }
    }, [definitionsQuery.data, callActivityErrorMessage]);
    const missingBinding =
        selection &&
        [FLUSS_NODE_TYPES.userTask, FLUSS_NODE_TYPES.serviceTask].includes(selection.type as any) &&
        !selection.businessObject?.extensionElements?.values?.some((value: any) => value.$type === "fluss:Binding");

    if (!canDesign) return <Navigate to="/home/fluss/requests" replace />;

    // A route change starts a new query but leaves the prior definition in local state for
    // one render. Never mount the new route with the previous version's canvas and modeler.
    if (definitionQuery.isLoading || !definition || definition.id !== definitionId) {
        return <Box className="fluss-page" display="grid" sx={{ placeItems: "center" }}><CircularProgress /></Box>;
    }

    if (definitionQuery.error) {
        return (
            <Box className="fluss-page">
                <Alert severity="error">{flussApiError(definitionQuery.error).message}</Alert>
            </Box>
        );
    }

    const startPropertiesResize = (event: ReactMouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = propertiesWidth;
        let nextWidth = startWidth;

        const applyWidth = () => {
            if (workspaceRef.current) {
                workspaceRef.current.style.gridTemplateColumns = `minmax(0, 1fr) 6px ${nextWidth}px`;
            }
        };

        const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
            // Panel is on the right, dragging the handle left (negative delta) grows it.
            nextWidth = clampPropertiesWidth(startWidth + (startX - moveEvent.clientX));
            applyWidth();
        };

        const handleMouseUp = () => {
            setPropertiesWidth(nextWidth);
            window.localStorage.setItem(FLUSS_PROPERTIES_WIDTH_STORAGE_KEY, String(nextWidth));
            document.body.classList.remove("fluss-studio-resizing");
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        document.body.classList.add("fluss-studio-resizing");
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const readOnly = definition.status !== "draft";
    const canDiscardCurrentDraft = canDiscardDraftPermission && definition.status === "draft" && !readOnly && definition.published_version != null;
    const blockingValidationIssues = hasBlockingIssues(validationErrors);
    const validationElementIds = validationErrors
        .map((error) => error.element_id)
        .filter((id): id is string => Boolean(id));

    return (
        <Box className="fluss-studio-shell">
            <FlussPageHeader
                // PRIM-002: the Name field itself was fixed to use TranslatableInput, but the
                // header title (this component, visible on every tab, not just "Design") still
                // rendered the raw stored value -- which is now a translation key rather than
                // literal text once someone attaches a translation. resolveDisplayText only
                // translates values that actually look like a `namespace.key` and passes plain
                // text through unchanged, so older/never-translated names keep working.
                title={resolveDisplayText(t, name || definition.name)}
                subtitle={<Box component="span" color="text.primary">{t("fluss.studio.version_key", {
                    key: definition.definition_key,
                    version: definition.version,
                    defaultValue: `${definition.definition_key} · v${definition.version}`,
                })}</Box>}
                status={(
                    <Stack direction="row" gap={1} alignItems="center">
                        <FlussStatusChip status={definition.status} />
                        {definition.disabled && (
                            <Chip
                                size="small"
                                color="warning"
                                label={t("fluss.repository.disabled_badge", { defaultValue: "Deactivated" })}
                            />
                        )}
                    </Stack>
                )}
                actions={(
                    <>
                        <Tooltip title={t("fluss.common.back", { defaultValue: "Back" })}>
                            <IconButton aria-label={String(t("fluss.common.back", { defaultValue: "Back" }))} onClick={() => navigate("/home/fluss")}><ArrowBack /></IconButton>
                        </Tooltip>
                        {!readOnly && <Tooltip title={t("fluss.studio.undo", { defaultValue: "Undo" })}>
                            <span><IconButton aria-label={String(t("fluss.studio.undo", { defaultValue: "Undo" }))} disabled={readOnly} onClick={() => canvasRef.current?.undo()}><Undo /></IconButton></span>
                        </Tooltip>}
                        {!readOnly && <Tooltip title={t("fluss.studio.redo", { defaultValue: "Redo" })}>
                            <span><IconButton aria-label={String(t("fluss.studio.redo", { defaultValue: "Redo" }))} disabled={readOnly} onClick={() => canvasRef.current?.redo()}><Redo /></IconButton></span>
                        </Tooltip>}
                        {!readOnly && <Tooltip title={t("fluss.common.refresh", { defaultValue: "Refresh" })}>
                            <IconButton aria-label={String(t("fluss.common.refresh", { defaultValue: "Refresh" }))} onClick={() => definitionQuery.refetch()}><Refresh /></IconButton>
                        </Tooltip>}
                        {readOnly && canDesign && (
                            <Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={() => setConfirmNewVersionOpen(true)}
                                sx={(theme) => ({
                                    color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark,
                                    borderColor: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark,
                                })}
                            >
                                {t("fluss.studio.new_version", { defaultValue: "Edit / New version" })}
                            </Button>
                        )}
                        {canDiscardCurrentDraft && (
                            <Button
                                variant="outlined"
                                startIcon={discarding ? <CircularProgress size={16} /> : <Restore />}
                                disabled={discarding}
                                onClick={() => setConfirmDiscardOpen(true)}
                            >
                                {t("fluss.studio.discard_draft", { defaultValue: "Discard draft" })}
                            </Button>
                        )}
                        {!readOnly && <Button
                            variant="outlined"
                            startIcon={saveMutation.isPending ? <CircularProgress size={16} /> : <Save />}
                            disabled={readOnly || saveMutation.isPending}
                            onClick={() => saveNow()}
                        >
                            {t("fluss.common.save", { defaultValue: "Save" })}
                        </Button>}
                        {canAutomate && !readOnly && (
                            <Button
                                startIcon={<Publish />}
                                disabled={readOnly}
                                onClick={() => validateMutation.mutate()}
                            >
                                {t("fluss.publish.review", { defaultValue: "Review publish" })}
                            </Button>
                        )}
                    </>
                )}
            />

            <Paper variant="outlined" sx={{ px: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                    <Tabs
                        value={mode}
                        onChange={(_, value) => changeMode(value)}
                        sx={(theme) => ({
                            ...(theme.palette.mode === "dark" && {
                                "& .MuiTab-root.Mui-selected": { color: theme.palette.primary.light },
                            }),
                        })}
                    >
                        <Tab value="design" label={t("fluss.mode.design", { defaultValue: "Design" })} />
                        {canAutomate && (
                            <Tab value="automation" label={t("fluss.mode.automation", { defaultValue: "Automation" })} />
                        )}
                    </Tabs>
                    <Stack direction="row" alignItems="center" gap={1}>
                        {saveError
                            ? <CloudOff color="error" />
                            : dirty || saveMutation.isPending ? <CloudOff color="warning" /> : <CloudDone color="success" />}
                        <Typography variant="caption" color={saveError ? "error" : "text.secondary"}>
                            {saveError
                                ? saveError
                                : dirty || saveMutation.isPending
                                    ? t("fluss.autosave.saving", { defaultValue: "Saving draft…" })
                                    : lastSavedAt
                                        ? t("fluss.autosave.saved", { defaultValue: "Draft saved" })
                                        : t("fluss.autosave.ready", { defaultValue: "Autosave ready" })}
                        </Typography>
                    </Stack>
                </Stack>
            </Paper>

            {conflict && (
                <Alert
                    severity="warning"
                    action={(
                        <Button color="inherit" size="small" onClick={() => reloadFromServer()}>
                            {t("fluss.conflict.reload", { defaultValue: "Reload latest" })}
                        </Button>
                    )}
                >
                    {t("fluss.conflict.message", {
                        defaultValue: "Another editor saved this definition. Reload before saving again.",
                    })}
                </Alert>
            )}

            {saveError && !conflict && (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={() => setSaveError(null)}>
                            {t("fluss.save_error.retry", { defaultValue: "Retry" })}
                        </Button>
                    )}
                >
                    {saveError}
                </Alert>
            )}

            {callActivityOpenError && (
                <Alert
                    severity="warning"
                    action={(
                        <Button color="inherit" size="small" onClick={() => setCallActivityOpenError(null)}>
                            {t("common.dismiss", { defaultValue: "Dismiss" })}
                        </Button>
                    )}
                >
                    {callActivityOpenError}
                </Alert>
            )}

            <Box
                className="fluss-studio-workspace"
                ref={workspaceRef}
                sx={{ gridTemplateColumns: `minmax(0, 1fr) 6px ${propertiesWidth}px` }}
            >
                <FlussCanvas
                    key={`${definition.id}:${canvasEpoch}`}
                    ref={canvasRef}
                    xml={studioDraftXml(definition.xml_draft || "", definition.definition_key, definition.name)}
                    onChanged={() => !readOnly && bumpEditVersion()}
                    onSelectionChanged={changeSelection}
                    onElementsChanged={syncSelectionWithModel}
                    onReady={onModelerReady}
                    validationElementIds={validationElementIds}
                    readOnly={readOnly}
                    canAuthorScript={canAuthorFlussScript(user)}
                    onOpenCallActivityTarget={openCallActivityTarget}
                />

                <Box
                    className="fluss-studio__resize-handle"
                    onMouseDown={startPropertiesResize}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={t("fluss.studio.resize_properties", { defaultValue: "Drag to adjust width" })}
                    title={t("fluss.studio.resize_properties", { defaultValue: "Drag to adjust width" })}
                />

                <Box className="fluss-properties-panel">
                    {mode === "design" ? (
                        <Stack gap={2}>
                            <Typography variant="h6">
                                {t("fluss.design.documentation", { defaultValue: "Process documentation" })}
                            </Typography>
                            <TranslatableInput
                                label={t("fluss.definition.name", { defaultValue: "Name" })}
                                value={name}
                                suggestedKey={`fluss_definition.${definition.definition_key || definition.id}.name`}
                                disabled={readOnly}
                                onChange={(value) => markDocumentChanged(() => setName(value))}
                            />
                            <TextField
                                label={t("fluss.definition.description", { defaultValue: "Description" })}
                                value={description}
                                disabled={readOnly}
                                multiline
                                minRows={4}
                                onChange={(event) => markDocumentChanged(() => setDescription(event.target.value))}
                            />
                            <UserSelect
                                disabled={readOnly}
                                value={ownerUserId}
                                onChange={(id) => markDocumentChanged(() => setOwnerUserId(id))}
                                label={t("fluss.design.owner", { defaultValue: "Process owner" })}
                            />
                            <TextField
                                label={t("fluss.design.raci", { defaultValue: "RACI responsibilities" })}
                                value={raci}
                                disabled={readOnly}
                                multiline
                                minRows={4}
                                onChange={(event) => markDocumentChanged(() => {
                                    setRaci(event.target.value);
                                    updateProcessConfig(modeler, { raci: event.target.value });
                                })}
                                helperText={t("fluss.design.raci_help", {
                                    defaultValue: "Describe who is responsible, accountable, consulted and informed.",
                                })}
                            />
                            <Divider />
                            <AttachmentPanel
                                tableName="sys_fluss_definitions"
                                recordId={definition.id}
                                title={String(t("fluss.design.attachments", { defaultValue: "Process attachments" }))}
                                disabled={readOnly}
                                collapsible
                            />
                        </Stack>
                    ) : newNodePanelsEnabled ? (
                        <FlussInlineIssuesProvider issues={validationErrors.filter((issue) => {
                            if (issue.element_id === selection?.id) return true;
                            const element = issue.element_id && modeler?.get?.("elementRegistry")?.get?.(issue.element_id);
                            return element?.source?.id === selection?.id;
                        })}><FlussNodePropertiesPanel
                            selection={selection}
                            modeler={modeler}
                            definitionKey={definition.definition_key}
                            onChanged={() => !readOnly && bumpEditVersion()}
                            canViewAdvanced={canViewAdvanced}
                            readOnly={readOnly}
                        /></FlussInlineIssuesProvider>
                    ) : (
                        <Stack gap={2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">
                                    {t("fluss.automation.title", { defaultValue: "Node automation" })}
                                </Typography>
                                <NodeValidationBadge count={missingBinding ? 1 : 0} />
                            </Stack>
                            <FlussAutomationPanel
                                selection={selection}
                                modeler={modeler}
                                onChanged={() => !readOnly && bumpEditVersion()}
                            />
                        </Stack>
                    )}
                </Box>
            </Box>

            <SidePanel
                open={publishPanelOpen}
                onClose={() => setPublishPanelOpen(false)}
                title={t("fluss.publish.title", { defaultValue: "Publish checklist" })}
                width={520}
            >
                <Stack gap={3}>
                    <LifecycleStepper activeStep={blockingValidationIssues ? 1 : 2} steps={lifecycleSteps} />
                    {validateMutation.isPending ? (
                        <CircularProgress />
                    ) : validationErrors.length === 0 ? (
                        <Alert severity="success">
                            {t("fluss.publish.valid", { defaultValue: "The definition passed all validation checks." })}
                        </Alert>
                    ) : (
                        <PendingIssuesPanel
                            issues={validationErrors}
                            onGoToNode={goToValidationNode}
                            onOpenCatalog={() => navigate("/home/records/sys_event_types")}
                            onOpenTemplate={() => navigate("/home/records/sys_email_templates")}
                        />
                    )}
                    {!blockingValidationIssues && (
                        <Button
                            onClick={() => setConfirmPublishOpen(true)}
                            color={validationErrors.length ? "warning" : "primary"}
                        >
                            {t("fluss.publish.action", { defaultValue: "Publish definition" })}
                        </Button>
                    )}
                </Stack>
            </SidePanel>

            <ConfirmActionDialog
                open={confirmDiscardOpen}
                title={t("fluss.studio.discard_draft_confirm_title", { defaultValue: "Discard this draft?" })}
                description={discardError || t("fluss.studio.discard_draft_confirm_description", {
                    defaultValue: "This restores the draft from the current published version. Your unpublished edits will be replaced.",
                })}
                confirmLabel={t("fluss.studio.discard_draft", { defaultValue: "Discard draft" })}
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                onConfirm={discardDraft}
                onClose={() => {
                    if (!discarding) {
                        setConfirmDiscardOpen(false);
                        setDiscardError(null);
                    }
                }}
                busy={discarding}
            />

            <ConfirmActionDialog
                open={confirmPublishOpen}
                title={t("fluss.publish.confirm_title", { defaultValue: "Publish this version?" })}
                description={t("fluss.publish.confirm_description", {
                    defaultValue: validationErrors.length
                        ? "This version has warnings. Confirm that you reviewed them before publishing."
                        : "A publicação guarda uma cópia e torna esta versão ativa para novas instâncias.",
                    context: validationErrors.length ? "warnings" : undefined,
                })}
                confirmLabel={t("fluss.publish.action", { defaultValue: "Publish definition" })}
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                onConfirm={() => publishMutation.mutate()}
                onClose={() => setConfirmPublishOpen(false)}
                busy={publishMutation.isPending}
            />

            <ConfirmActionDialog
                open={confirmNewVersionOpen}
                title={t("fluss.studio.new_version_confirm_title", { defaultValue: "Create a new editable version?" })}
                description={(
                    <Stack gap={1}>
                        <Typography color="text.secondary">
                            {t("fluss.studio.new_version_confirm_description", {
                                defaultValue: "This clones the published diagram into a new draft (v{{next}}). The current published version and its running instances are not affected.",
                                next: definition.version + 1,
                            })}
                        </Typography>
                        {newVersionMutation.error && (
                            <Alert severity="error">{flussApiError(newVersionMutation.error).message}</Alert>
                        )}
                    </Stack>
                )}
                confirmLabel={t("fluss.studio.new_version", { defaultValue: "Edit / New version" })}
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                onConfirm={() => newVersionMutation.mutate()}
                onClose={() => setConfirmNewVersionOpen(false)}
                busy={newVersionMutation.isPending}
            />
        </Box>
    );
};

export default FlussStudioPage;
